import type { SearchCacheBackend } from "@pdfking/compatibility"
import { Map } from "@pdfking/tfunc"

const store = "Default" as const
const version = 3

const to_promise = (request: IDBRequest): Promise<any> =>
  new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })

export const MetaCache = <V>(database: string): SearchCacheBackend<V> => {
  const open = (
    mode: "readonly" | "readwrite" = "readwrite"
  ): Promise<IDBObjectStore> => {
    const request = indexedDB.open(database, version)
    request.onupgradeneeded = () => {
      const db = request.result

      console.info(`MetaCache: Upgrading database "${database}" to ${version}`)

      if (db.objectStoreNames.contains(store)) {
        db.deleteObjectStore(store)
        console.info(`MetaCache: Cleared existing data in "${database}"`)
      }

      db.createObjectStore(store, { keyPath: "id" })
    }
    return to_promise(request).then((result: IDBDatabase) =>
      result.transaction(store, mode).objectStore(store)
    )
  }

  return {
    clear: (): Promise<void> => open().then(db => to_promise(db.clear())),

    delete: (id: string): Promise<void> =>
      open().then(db => to_promise(db.delete(id))),

    exists: (id: string): Promise<boolean> =>
      open("readonly")
        .then(db => to_promise(db.get(id)))
        .then(result => !!result),

    get: (id: string): Promise<undefined | V> =>
      open("readonly")
        .then(db => to_promise(db.get(id)))
        .then(result => (result ? result.data : undefined)),

    set: (id: string, data: V): Promise<void> =>
      open().then(db =>
        to_promise(
          db.put({
            data,
            id,
          })
        )
      ),

    startswith: (prefix?: string): Promise<Record<string, V>> =>
      open("readonly").then(db =>
        to_promise(
          db.getAll(
            prefix ? IDBKeyRange.bound(prefix, `${prefix}\uffff`) : undefined
          )
        ).then((results: { data: V; id: string }[]) =>
          Object.fromEntries(Map(({ data, id }) => [id, data], results))
        )
      ),
  }
}
